/**
 * Tâche 1143 (Eric 2026-06-16) — Journal des envois d'alertes.
 *
 * « Tu envoies encore ce courriel à Vincent alors que la case est décochée » :
 * impossible de répondre sans trace. Chaque tentative est consignée, y compris
 * celles qui ne partent pas — un envoi absent doit s'expliquer (en pause, sans
 * destinataire, refusé par le fournisseur, environnement sans courriel).
 *
 * Rétention : 90 jours (choix d'Eric).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  CaretDown,
  CaretRight,
  ClockCounterClockwise,
  DownloadSimple,
} from "@phosphor-icons/react";
// Tâche 1146 — L'audit vit en haut de cette page (choix d'Eric).
import RecipientAuditPanel from "../../components/admin/RecipientAuditPanel";
// Tâche 1149 — Refus du fournisseur, avec le motif exact.
import ProviderRefusalPanel from "../../components/admin/ProviderRefusalPanel";
// Tâche 1151 — Cause racine de la plupart de ces refus : au-dessus du reste.
import EmailDomainGuide from "../../components/admin/EmailDomainGuide";
// Tâche 1152 — Quota d'envoi : prévenir avant que Resend refuse tout.
import ResendQuotaPanel from "../../components/admin/ResendQuotaPanel";
// Tâche 1156 — Vérifier qu'un courriel part vraiment, sans quitter l'écran.
import SelfTestEmailButton from "../../components/admin/SelfTestEmailButton";

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABEL = {
  sent: "Envoyé",
  failed: "Refusé",
  skipped: "Non envoyé",
  deferred: "Reporté (lissage)",
};

const QUEUE_LABEL = {
  pending: "En attente d'une nouvelle tentative",
  sent: "Finalement envoyé",
  abandoned: "Abandonné",
};

const JourneyTimeline = ({ journey }) => {
  if (!journey) {
    return (
      <p className="text-xs text-zinc-500" data-testid="journey-loading">
        Chargement du parcours…
      </p>
    );
  }
  if (!journey.steps?.length) {
    return (
      <p className="text-xs text-zinc-500" data-testid="journey-empty">
        Une seule tentative : rien d'autre à raconter.
      </p>
    );
  }
  return (
    <div data-testid="journey-timeline">
      <ol className="space-y-1.5">
        {journey.steps.map((s, i) => (
          <li
            key={`${s.at}-${i}`}
            className="flex flex-wrap items-baseline gap-2 text-xs"
            data-testid={`journey-step-${i}`}
          >
            <span className="text-zinc-500 w-32 shrink-0">
              {(s.at || "").slice(0, 16).replace("T", " ")}
            </span>
            <span
              className={`px-1.5 py-0.5 rounded font-medium ${
                STATUS_STYLE[s.status] || "bg-zinc-100 text-zinc-600"
              }`}
            >
              {STEP_LABEL[s.status] || s.status}
            </span>
            {s.attempt > 0 && (
              <span className="text-zinc-600">réessai {s.attempt}</span>
            )}
            <span className="text-zinc-500">{s.reason}</span>
          </li>
        ))}
      </ol>
      {journey.queue && (
        <p className="mt-2 text-xs text-zinc-600" data-testid="journey-queue">
          <strong>{QUEUE_LABEL[journey.queue.status] || journey.queue.status}</strong>
          {journey.queue.status === "pending" && journey.queue.next_attempt_at
            ? ` — prochaine tentative : ${journey.queue.next_attempt_at
                .slice(0, 16)
                .replace("T", " ")}`
            : ""}
          {` · tentative ${journey.queue.attempt}/${journey.queue.max_attempts}`}
          {journey.queue.smoothed ? " · mis en attente pour lisser la cadence" : ""}
        </p>
      )}
    </div>
  );
};

const STATUS_STYLE = {
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  skipped: "bg-zinc-100 text-zinc-600",
};

export default function EmailSendLogPage() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    org_id: "",
    family: "",
    recipient: "",
    days: 90,
  });
  const [exporting, setExporting] = useState(false);
  // Tâche 1154 — Parcours d'un courriel : le journal montrait trois lignes
  // isolées (« refusé », « refusé », « envoyé ») sans dire qu'il s'agissait du
  // même courriel rejoué.
  const [openTrace, setOpenTrace] = useState(null);
  const [journeys, setJourneys] = useState({});

  const toggleJourney = async (traceId) => {
    if (openTrace === traceId) {
      setOpenTrace(null);
      return;
    }
    setOpenTrace(traceId);
    if (journeys[traceId]) return;
    try {
      const res = await axios.get(
        `${API}/api/system-notifications/email-journey/${traceId}`,
        { withCredentials: true }
      );
      setJourneys((prev) => ({ ...prev, [traceId]: res.data }));
    } catch {
      setJourneys((prev) => ({ ...prev, [traceId]: { steps: [] } }));
    }
  };

  const query = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p.set(k, String(v));
    });
    return p.toString();
  }, [filters]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/api/system-notifications/send-log?${query()}`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch {
      setData(null);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/api/system-notifications/send-log/csv?${query()}`,
        { withCredentials: true, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `journal-envois-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const set = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="p-6 max-w-6xl" data-testid="email-send-log-page">
      <EmailDomainGuide />
      <ResendQuotaPanel />
      <ProviderRefusalPanel />
      <RecipientAuditPanel />

      <h1 className="text-2xl font-semibold text-zinc-900 flex items-center gap-2">
        <ClockCounterClockwise size={24} />
        Journal des envois d'alertes
      </h1>
      <p className="text-sm text-zinc-500 mt-1 mb-5 max-w-3xl">
        Chaque courriel système, parti ou non. Les envois <strong>non
        envoyés</strong> sont affichés avec leur motif : un trou inexpliqué
        serait pire qu'une ligne « non envoyé ». Conservé{" "}
        {data?.retention_days || 90} jours.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-zinc-600">
          Organisation
          <select
            value={filters.org_id}
            onChange={set("org_id")}
            className="mt-1 block rounded-lg border border-zinc-300 px-3 py-2 text-sm"
            data-testid="send-log-filter-org"
          >
            <option value="">Toutes</option>
            {(data?.organizations || []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-zinc-600">
          Famille
          <select
            value={filters.family}
            onChange={set("family")}
            className="mt-1 block rounded-lg border border-zinc-300 px-3 py-2 text-sm"
            data-testid="send-log-filter-family"
          >
            <option value="">Toutes</option>
            {(data?.families || []).map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-zinc-600">
          Destinataire
          <input
            value={filters.recipient}
            onChange={set("recipient")}
            placeholder="adresse@exemple.com"
            className="mt-1 block rounded-lg border border-zinc-300 px-3 py-2 text-sm"
            data-testid="send-log-filter-recipient"
          />
        </label>
        <label className="text-xs text-zinc-600">
          Période
          <select
            value={filters.days}
            onChange={set("days")}
            className="mt-1 block rounded-lg border border-zinc-300 px-3 py-2 text-sm"
            data-testid="send-log-filter-days"
          >
            <option value={1}>24 dernières heures</option>
            <option value={7}>7 derniers jours</option>
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
          </select>
        </label>
        <button
          type="button"
          onClick={exportCsv}
          disabled={exporting || !data?.entries?.length}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="send-log-export-csv"
        >
          <DownloadSimple size={16} />
          {exporting ? "Export…" : "Exporter en CSV"}
        </button>
        <SelfTestEmailButton />
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">État</th>
              <th className="text-left px-3 py-2">Famille</th>
              <th className="text-left px-3 py-2">Organisation</th>
              <th className="text-left px-3 py-2">Destinataire</th>
              <th className="text-left px-3 py-2">Objet</th>
              <th className="text-left px-3 py-2">Motif</th>
              <th className="text-left px-3 py-2">Parcours</th>
            </tr>
          </thead>
          <tbody data-testid="send-log-rows">
            {(data?.entries || []).map((e) => [
              <tr key={e.id} className="border-t border-zinc-100">
                <td className="px-3 py-2 whitespace-nowrap text-zinc-500">
                  {(e.sent_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      STATUS_STYLE[e.status] || "bg-zinc-100 text-zinc-600"
                    }`}
                  >
                    {(data?.status_labels || {})[e.status] || e.status}
                  </span>
                </td>
                <td className="px-3 py-2">{e.family_label}</td>
                <td className="px-3 py-2 text-zinc-600">{e.org_name}</td>
                <td className="px-3 py-2">{e.intended_recipient}</td>
                <td className="px-3 py-2 text-zinc-600">{e.subject}</td>
                <td className="px-3 py-2 text-zinc-500">{e.reason}</td>
                <td className="px-3 py-2">
                  {e.trace_id ? (
                    <button
                      type="button"
                      onClick={() => toggleJourney(e.trace_id)}
                      className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:text-zinc-900"
                      data-testid={`journey-toggle-${e.id}`}
                    >
                      {openTrace === e.trace_id ? (
                        <CaretDown size={12} />
                      ) : (
                        <CaretRight size={12} />
                      )}
                      Parcours
                    </button>
                  ) : (
                    <span className="text-xs text-zinc-400">—</span>
                  )}
                </td>
              </tr>,
              openTrace === e.trace_id ? (
                <tr key={`${e.id}-journey`} className="bg-zinc-50">
                  <td colSpan={8} className="px-6 py-3">
                    <JourneyTimeline journey={journeys[e.trace_id]} />
                  </td>
                </tr>
              ) : null,
            ]).flat().filter(Boolean)}
            {!data?.entries?.length && (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-6 text-center text-sm text-zinc-500"
                  data-testid="send-log-empty"
                >
                  Aucun envoi sur cette période avec ces filtres.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
